import React, { useState, useEffect } from 'react';

import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setChapterLangList } from '@modules/chapter';
import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import AutoFixHigh from '@mui/icons-material/AutoFixHigh';
import api from '@utils/axiosInstance';
import { useTranslation } from 'react-i18next';
import { setSelectedLanguage } from '@modules/chapter';
// react-toastify 관련 import 추가
import { toast, Flip } from 'react-toastify';

var allowedLang = [
    'ko',
    'en',
    'ja',
    'zh-CN',
    'zh-TW',
    'vi',
    'id',
    'th',
    'de',
    'ru',
    'es',
    'it',
    'fr'
]

function NoChapContent({
    availableChapterLanguages,
    setAvailableChapterLanguages,
    setCurrentSelectedLanguage,
    setIsAddModalOpen,
    setShowNoChap
}) {
    const dispatch = useDispatch();
    const { uuid } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const { userToken, organizationData } = useSelector(state => state.organization);

    const [selectedChapterLanguage, setSelectedChapterLanguage] = useState(null);
    const [availableLanguageOptions, setAvailableLanguageOptions] = useState([]);
    const [isChapterCreationInProgress, setIsChapterCreationInProgress] = useState(false);

    // 자막 생성되지 않은 언어 필터링
    useEffect(() => {
        const filtered = allowedLang.filter(
            lang => !availableChapterLanguages.some(chap => chap.lang === lang)
        );
        setAvailableLanguageOptions(filtered);
    }, [availableChapterLanguages]);

    const handleCreateChapter = () => {
        if (!selectedChapterLanguage) {
            toast.error(t("vod.manage.chapter_tab.select_language", {
                position: "top-center",
                transition: Flip
            }));
            return;
        }

        setIsChapterCreationInProgress(true);
        const toastId = toast.info(t("vod.manage.chapter_tab.creating_empty_chapter"), {
            position: "top-center",
            transition: Flip
        });

        try {
            api({
                method: 'post',
                url: '/chapter/create',
                data: {
                    tk: userToken,
                    uuid,
                    ogc: organizationData.organization_code,
                    lang: selectedChapterLanguage
                }
            })
                .then(response => {
                    if (response.data.code !== 100) {
                        throw new Error(t("vod.manage.chapter_tab.chapter_creation_failed"));
                    } else {
                        const newChapter = { uuid, lang: selectedChapterLanguage, state: 0 };
                        setAvailableChapterLanguages(prev => [...prev, newChapter]);
                        setCurrentSelectedLanguage(newChapter);
                        dispatch(setSelectedLanguage(newChapter));
                        setIsAddModalOpen(false);
                        setShowNoChap(false);
                        setAvailableLanguageOptions([]);
                        toast.dismiss(toastId);
                        toast.success(t("vod.manage.chapter_tab.empty_chapter_created"), {
                            position: "top-center",
                            transition: Flip
                        });

                        const pathSegments = location.pathname.split('/');
                        const chapterIndex = pathSegments.findIndex(segment => segment === 'chapter');
                        console.log('pathSegments', pathSegments, chapterIndex);
                        if (chapterIndex !== -1) {
                            const newPath = pathSegments.slice(0, chapterIndex + 1).join('/') + '/' + selectedChapterLanguage;
                            console.log('newPath', newPath);
                            navigate(newPath, { replace: true });
                        }
                    }
                })
        } catch (error) {
            toast.dismiss(toastId);
            toast.error(error.message || t("common.error_occurred"), {
                position: "top-center",
                transition: Flip
            });
        }
    };

    // 자동 생성은 추후
    // const handleAutoCreateChapter = () => {
    //     if (!chapterLanguage) {
    //         setSnackbar({ open: true, message: t("vod.manage.chapter_tab.select_language"), severity: 'error' });
    //         return;
    //     }

    //     setIsChapterCreationInProgress(true);
    //     setSnackbar({ open: true, message: t("vod.manage.chapter_tab.auto_creating_chapter"), severity: 'info' });

    //     api({
    //         method: 'post',
    //         url: '/chapter/generate',
    //         data: {
    //             tk: userToken,
    //             uuid,
    //             ogc: organizationData.organization_code,
    //             lang: chapterLanguage
    //         }
    //     })
    //         .then(response => {
    //             if (response.data.code === 100) {
    //                 setSnackbar({ open: true, message: t("vod.manage.chapter_tab.chapter_auto_created"), severity: 'success' });
    //                 setNoChap(false);
    //                 dispatch(setChapterLangList([
    //                     ...chapterLangList,
    //                     selectedLang
    //                 ]))
    //                 setShowChapModal(false);
    //             } else {
    //                 throw new Error(t("vod.manage.chapter_tab.auto_creation_failed"));
    //             }
    //         })
    //         .catch(error => {
    //             setSnackbar({ open: true, message: error.message || t("common.error_occurred"), severity: 'error' });
    //         })
    //         .finally(() => {
    //             setIsLoading(false);
    //         });
    // };

    return (
        <Box className="container-no-chap">
            <Box className="container-no-chap-content">
                <Typography variant="h6" className="text-no-chap-title">
                    {t("vod.manage.chapter_tab.chapter_creation")}
                </Typography>

                <Typography variant="body1" className="text-no-chap-description">
                    {t("vod.manage.chapter_tab.select_language")}
                </Typography>

                <FormControl variant="outlined" className="form-no-chap-select">
                    <Autocomplete
                        options={availableLanguageOptions}
                        getOptionLabel={(opt) => t(`languages.${opt.replace('-', '_').toLowerCase()}`)}
                        onChange={(e, newValue) => setSelectedChapterLanguage(newValue)}
                        noOptionsText={t('vod.manage.subtitle_tab.no_matching_data')}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('vod.manage.subtitle_tab.selected_language')}
                                className="input-no-chap-autocomplete"
                            />
                        )}
                    />
                </FormControl>

                <Typography variant="body1" className="text-no-chap-selected">
                    {selectedChapterLanguage ? `${t('vod.manage.chapter_tab.selected_language')} ${t(`languages.${selectedChapterLanguage}`)}` : ''}
                </Typography>

                <Box className="container-no-chap-buttons">
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        color="info"
                        onClick={handleCreateChapter}
                        disabled={isChapterCreationInProgress || !selectedChapterLanguage}
                        fullWidth
                    >
                        {isChapterCreationInProgress ? t("vod.manage.subtitle_tab.loading") : t("vod.manage.chapter_tab.create_empty_chapter")}
                    </Button>
                    {/* <Button
                            className="chapEditBtn"
                            variant="contained"
                            startIcon={<AutoFixHigh />}
                            color="primary"
                            onClick={handleAutoCreateChapter}
                            disabled={isLoading || !selectedChapterLanguage}
                            fullWidth
                        >
                            {isLoading ? t("vod.manage.chapter_tab.auto_creating_chapter") : t("vod.manage.chapter_tab.auto_create_chapter")}
                        </Button> */}
                </Box>

            </Box>
        </Box>
    );
}

export default NoChapContent;
